import CircularProgress from "@material-ui/core/CircularProgress"
import ErrorList from "../../../components/ErrorList"
import TaskListMobile from "../../../tasks/components/TaskListMobile"
import TaskTreeGrid from "../../../treegrid/task-tree-grid/TaskTreeGrid"
import { RouteComponentProps } from "react-router-dom"
import { useTask } from "../../../tasks/hooks/use-task"
import { useGetTasksQuery } from "../../../tasks/api"
import { useIsSmallScreen } from "../../../hooks/use-is-small-screen"
import { useTranslation } from "react-i18next"
import { useProjectCalendarSettings } from "../../providers/project-calendar-settings"

type RouteParams = { taskId: string }

const queryOptions = { refetchOnMountOrArgChange: true } as const

const TaskSubtasksRoute = ({ match }: RouteComponentProps<RouteParams>) => {
  const isSmallScreen = useIsSmallScreen()
  const translations = useTranslations()
  const { taskId } = match.params
  const { task, bulkAddTasks, isLoading: isLoadingTask, isError: isErrorTask } = useTask(taskId, queryOptions)
  const tasksQuery = { task: taskId, archived: "false", links: "true" } as const
  const {
    data: subtasks = [],
    isLoading: isLoadingSubtasks,
    isError: isErrorSubtasks,
  } = useGetTasksQuery(tasksQuery, queryOptions)
  const { calendarSettings } = useProjectCalendarSettings()

  if (isLoadingTask || isLoadingSubtasks) return <CircularProgress />
  if (isErrorTask) return <ErrorList errors={[translations.fetchErrorTask]} />
  if (isErrorSubtasks) return <ErrorList errors={[translations.fetchErrorTasks]} />
  if (!task) return <ErrorList errors={[translations.taskNotFoundError]} />

  const descendants = subtasks.filter((subtask) => subtask.ancestors.includes(task.id))

  return isSmallScreen ? (
    <TaskListMobile rootTaskId={taskId} tasks={descendants} canAddTasks={task.canCreate} onAddTask={bulkAddTasks} />
  ) : (
    <TaskTreeGrid task={task} tasks={subtasks} calendarSettings={calendarSettings} onAddTask={bulkAddTasks} />
  )
}

const defaultTranslations = {
  fetchErrorTask: "Failed to fetch task",
  fetchErrorTasks: "Failed to fetch tasks",
  taskNotFoundError: "Task not found",
}

const useTranslations = (): typeof defaultTranslations => {
  const { t } = useTranslation("translation")

  return {
    fetchErrorTask: t("fetchErrorTask", defaultTranslations.fetchErrorTask),
    fetchErrorTasks: t("fetchErrorTasks", defaultTranslations.fetchErrorTasks),
    taskNotFoundError: t("taskNotFoundError", defaultTranslations.taskNotFoundError),
  }
}

export default TaskSubtasksRoute
